import { useEffect, useRef, useState } from 'preact/hooks';
import { useOddball } from '../midi-context';
import { getAudioContext, createReverbBuffer, createNoiseBuffer } from '../audio/audio-engine';
import { useCCValues } from '../hooks/use-oddball-events';
import type { GestureEvent, TapEvent, ShakeEvent, TwistEvent, CCUpdateEvent } from '../../src/types';
import { OddballCC } from '../../src/types';

// Melodic scale: C major pentatonic across octaves
const MELODY_SCALE = [261.63, 293.66, 329.63, 392.00, 440.00, 523.25, 587.33, 659.25];
const NOTE_NAMES = ['C4', 'D4', 'E4', 'G4', 'A4', 'C5', 'D5', 'E5'];

// ── Beat presets (16-step patterns, velocity 0–3: off / ghost / normal / accent) ──

interface BeatPreset {
  name: string;
  kick:  number[];
  snare: number[];
  hat:   number[];
}

const BEAT_PRESETS: BeatPreset[] = [
  { name: 'Basic',
    kick:  [3,0,0,0, 2,0,0,0, 3,0,0,0, 2,0,0,0],
    snare: [0,0,0,0, 3,0,0,0, 0,0,0,0, 3,0,0,0],
    hat:   [2,0,1,0, 2,0,1,0, 2,0,1,0, 2,0,1,0] },
  { name: 'Rock',
    kick:  [3,0,0,0, 0,0,0,0, 3,0,2,0, 0,0,0,0],
    snare: [0,0,0,0, 3,0,0,0, 0,0,0,0, 3,0,0,1],
    hat:   [2,0,2,0, 2,0,2,0, 2,0,2,0, 2,0,2,1] },
  { name: 'Funk',
    kick:  [3,0,0,2, 0,0,2,0, 0,2,0,0, 3,0,0,0],
    snare: [0,0,0,0, 3,0,0,1, 0,0,1,0, 3,0,1,0],
    hat:   [2,2,2,2, 2,2,2,2, 2,2,2,2, 2,2,2,2] },
  { name: 'Reggae',
    kick:  [0,0,0,0, 0,0,3,0, 0,0,0,0, 0,0,3,0],
    snare: [0,0,0,3, 0,0,0,0, 0,0,0,3, 0,0,0,0],
    hat:   [1,0,2,0, 1,0,2,0, 1,0,2,0, 1,0,2,0] },
  { name: 'Shuffle',
    kick:  [3,0,0,0, 0,2,0,0, 3,0,0,0, 0,2,0,0],
    snare: [0,0,0,0, 3,0,0,0, 0,0,0,0, 3,0,0,2],
    hat:   [3,0,1,3, 0,1,3,0, 1,3,0,1, 3,0,1,0] },
];

// ── Drum synthesis ──

function playKick(ctx: AudioContext, dest: AudioNode, time: number, velocity: number) {
  const vol = [0, 0.15, 0.28, 0.42][velocity];
  const osc = ctx.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(150, time);
  osc.frequency.exponentialRampToValueAtTime(40, time + 0.08);
  const g = ctx.createGain();
  g.gain.setValueAtTime(vol, time);
  g.gain.exponentialRampToValueAtTime(0.001, time + 0.2);
  osc.connect(g).connect(dest);
  osc.start(time);
  osc.stop(time + 0.25);
}

function playSnare(ctx: AudioContext, dest: AudioNode, noiseBuffer: AudioBuffer, time: number, velocity: number) {
  const vol = [0, 0.1, 0.2, 0.3][velocity];
  // Noise body
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuffer;
  const nf = ctx.createBiquadFilter();
  nf.type = 'bandpass';
  nf.frequency.value = 3000;
  nf.Q.value = 1;
  const ng = ctx.createGain();
  ng.gain.setValueAtTime(vol, time);
  ng.gain.exponentialRampToValueAtTime(0.001, time + 0.1);
  noise.connect(nf).connect(ng).connect(dest);
  noise.start(time);
  noise.stop(time + 0.12);
  // Tone snap
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.value = 200;
  const og = ctx.createGain();
  og.gain.setValueAtTime(vol * 0.5, time);
  og.gain.exponentialRampToValueAtTime(0.001, time + 0.04);
  osc.connect(og).connect(dest);
  osc.start(time);
  osc.stop(time + 0.05);
}

function playHat(ctx: AudioContext, dest: AudioNode, noiseBuffer: AudioBuffer, time: number, velocity: number) {
  const vol = [0, 0.06, 0.12, 0.2][velocity];
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuffer;
  const f = ctx.createBiquadFilter();
  f.type = 'highpass';
  f.frequency.value = 7000;
  const g = ctx.createGain();
  g.gain.setValueAtTime(vol, time);
  g.gain.exponentialRampToValueAtTime(0.001, time + 0.04);
  noise.connect(f).connect(g).connect(dest);
  noise.start(time);
  noise.stop(time + 0.05);
}

function mapRange(value: number, inMin: number, inMax: number, outMin: number, outMax: number): number {
  return outMin + ((value - inMin) / (inMax - inMin)) * (outMax - outMin);
}

interface AxisEngine {
  ctx: AudioContext;
  masterGain: GainNode;
  reverb: ConvolverNode;
  wetGain: GainNode;
  melodyOscs: OscillatorNode[];
  melodyGains: GainNode[];
  melodyFilter: BiquadFilterNode;
  melodyMaster: GainNode;
  rhythmMaster: GainNode;
  rhythmInterval: number;
  beatPresetIndex: number;
  beatStep: number;
  droneOscs: OscillatorNode[];
  droneGains: GainNode[];
  droneMaster: GainNode;
  droneFilter: BiquadFilterNode;
  noiseBuffer: AudioBuffer;
  octaveShift: number;
  speedMul: number;
}

function createAxisEngine(): AxisEngine {
  const ctx = getAudioContext();
  const masterGain = ctx.createGain();
  masterGain.gain.value = 0.35;

  const reverb = ctx.createConvolver();
  reverb.buffer = createReverbBuffer(ctx, 2, 2);
  const wetGain = ctx.createGain();
  wetGain.gain.value = 0.3;
  const dryGain = ctx.createGain();
  dryGain.gain.value = 0.7;
  masterGain.connect(dryGain).connect(ctx.destination);
  masterGain.connect(reverb).connect(wetGain).connect(ctx.destination);

  const melodyFilter = ctx.createBiquadFilter();
  melodyFilter.type = 'lowpass';
  melodyFilter.frequency.value = 3000;
  const melodyMaster = ctx.createGain();
  melodyMaster.gain.value = 0;
  melodyFilter.connect(melodyMaster).connect(masterGain);

  const osc0 = ctx.createOscillator();
  osc0.type = 'sine';
  osc0.frequency.value = MELODY_SCALE[0];
  const g0 = ctx.createGain();
  g0.gain.value = 0.2;
  osc0.connect(g0).connect(melodyFilter);
  osc0.start();

  const rhythmMaster = ctx.createGain();
  rhythmMaster.gain.value = 0;
  rhythmMaster.connect(masterGain);

  const droneFilter = ctx.createBiquadFilter();
  droneFilter.type = 'lowpass';
  droneFilter.frequency.value = 800;
  const droneMaster = ctx.createGain();
  droneMaster.gain.value = 0;
  droneFilter.connect(droneMaster).connect(masterGain);

  const droneOsc0 = ctx.createOscillator();
  droneOsc0.type = 'sawtooth';
  droneOsc0.frequency.value = 110;
  const droneG0 = ctx.createGain();
  droneG0.gain.value = 0.08;
  droneOsc0.connect(droneG0).connect(droneFilter);
  droneOsc0.start();

  const noiseBuffer = createNoiseBuffer(ctx, 1);

  return {
    ctx, masterGain, reverb, wetGain,
    melodyOscs: [osc0], melodyGains: [g0], melodyFilter, melodyMaster,
    rhythmMaster, rhythmInterval: 0, beatPresetIndex: 0, beatStep: 0,
    droneOscs: [droneOsc0], droneGains: [droneG0], droneMaster, droneFilter,
    noiseBuffer,
    octaveShift: 0, speedMul: 1,
  };
}

function addMelodyVoice(eng: AxisEngine) {
  if (eng.melodyOscs.length >= 3) return;
  const osc = eng.ctx.createOscillator();
  osc.type = 'sine';
  const baseFreq = eng.melodyOscs[0].frequency.value;
  const intervals = [1.25, 1.5];
  osc.frequency.value = baseFreq * intervals[eng.melodyOscs.length - 1];
  const g = eng.ctx.createGain();
  g.gain.value = 0.15;
  osc.connect(g).connect(eng.melodyFilter);
  osc.start();
  eng.melodyOscs.push(osc);
  eng.melodyGains.push(g);
}

function addDroneLayer(eng: AxisEngine) {
  if (eng.droneOscs.length >= 5) return;
  const osc = eng.ctx.createOscillator();
  osc.type = 'sawtooth';
  const detune = (eng.droneOscs.length - Math.floor(eng.droneOscs.length / 2)) * 3;
  osc.frequency.value = 110;
  osc.detune.value = detune;
  const g = eng.ctx.createGain();
  g.gain.value = 0.05;
  osc.connect(g).connect(eng.droneFilter);
  osc.start();
  eng.droneOscs.push(osc);
  eng.droneGains.push(g);
}

function startBeatSequencer(eng: AxisEngine, stepMs: number) {
  clearInterval(eng.rhythmInterval);
  eng.beatStep = 0;
  const tick = () => {
    const preset = BEAT_PRESETS[eng.beatPresetIndex];
    const s = eng.beatStep % 16;
    const t = eng.ctx.currentTime;
    if (preset.kick[s] > 0) playKick(eng.ctx, eng.rhythmMaster, t, preset.kick[s]);
    if (preset.snare[s] > 0) playSnare(eng.ctx, eng.rhythmMaster, eng.noiseBuffer, t, preset.snare[s]);
    if (preset.hat[s] > 0) playHat(eng.ctx, eng.rhythmMaster, eng.noiseBuffer, t, preset.hat[s]);
    eng.beatStep++;
  };
  tick();
  eng.rhythmInterval = window.setInterval(tick, stepMs);
}

function getDominantAxis(x: number, y: number, z: number): 'x' | 'y' | 'z' {
  const dx = Math.abs(x - 64);
  const dy = Math.abs(y - 64);
  const dz = Math.abs(z - 64);
  if (dx >= dy && dx >= dz) return 'x';
  if (dy >= dx && dy >= dz) return 'y';
  return 'z';
}

// Sparkline showing deviation from center
function DeviationSparkline({ data, color }: { data: number[]; color: string }) {
  const w = 100;
  const h = 32;
  if (data.length < 2) return <svg width={w} height={h} />;
  const points = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h / 2 - ((v - 64) / 64) * (h / 2);
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <line x1={0} y1={h / 2} x2={w} y2={h / 2} stroke="#222" stroke-width="1" />
      <polyline points={points} fill="none" stroke={color} stroke-width="1.5" opacity="0.8" />
    </svg>
  );
}

// Horizontal bar centered at midpoint, showing deviation direction + magnitude
function DeviationBar({ value, color, isDominant }: { value: number; color: string; isDominant: boolean }) {
  const deviation = (value - 64) / 64; // -1 to 1
  const barW = 140;
  const mid = barW / 2;
  const fillW = Math.abs(deviation) * mid;
  const left = deviation >= 0 ? mid : mid - fillW;
  return (
    <div style={{ position: 'relative', width: `${barW}px`, height: '10px', background: '#1a1a1a', borderRadius: '5px', overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', left: `${mid}px`, top: 0, bottom: 0,
        width: '1px', background: '#333',
      }} />
      <div style={{
        position: 'absolute', left: `${left}px`, top: 0, bottom: 0,
        width: `${fillW}px`,
        background: color,
        opacity: isDominant ? 0.9 : 0.4,
        borderRadius: '5px',
        transition: 'all 0.05s',
      }} />
    </div>
  );
}

// Dominance mix bar — shows relative strength of all 3 axes
function DominanceMix({ x, y, z, colors }: { x: number; y: number; z: number; colors: [string, string, string] }) {
  const dx = Math.abs(x - 64);
  const dy = Math.abs(y - 64);
  const dz = Math.abs(z - 64);
  const total = Math.max(dx + dy + dz, 1);
  const px = (dx / total) * 100;
  const py = (dy / total) * 100;
  return (
    <div style={{ width: '200px', height: '8px', borderRadius: '4px', overflow: 'hidden', display: 'flex' }}>
      <div style={{ width: `${px}%`, background: colors[0], transition: 'width 0.1s' }} />
      <div style={{ width: `${py}%`, background: colors[1], transition: 'width 0.1s' }} />
      <div style={{ flex: 1, background: colors[2], transition: 'width 0.1s' }} />
    </div>
  );
}

const HISTORY_LEN = 60;

export function AxisExplorer() {
  const { parser, detector, connected, speedModifier, octaveOffset } = useOddball();
  const cc = useCCValues();
  const engineRef = useRef<AxisEngine | null>(null);
  const speedRef = useRef(1.0);
  const octaveRef = useRef(0);

  useEffect(() => { speedRef.current = speedModifier; }, [speedModifier]);
  useEffect(() => { octaveRef.current = octaveOffset; }, [octaveOffset]);
  useEffect(() => {
    const eng = engineRef.current;
    if (eng) eng.masterGain.gain.setTargetAtTime(connected ? 0.35 : 0, eng.ctx.currentTime, 0.05);
  }, [connected]);

  const [xVoices, setXVoices] = useState(1);
  const [beatPreset, setBeatPreset] = useState(0);
  const [zLayers, setZLayers] = useState(1);
  const [totalInteractions, setTotalInteractions] = useState(0);
  const [dominant, setDominant] = useState<'x' | 'y' | 'z'>('x');
  const [lastGesture, setLastGesture] = useState<{ type: string; axis: string; time: number } | null>(null);

  // History for sparklines
  const histRef = useRef<{ x: number[]; y: number[]; z: number[] }>({ x: [], y: [], z: [] });
  const [, setHistTick] = useState(0);

  const dominantRef = useRef<'x' | 'y' | 'z'>('x');
  const totalRef = useRef(0);
  const stepMsRef = useRef(150);
  const tapTimesRef = useRef<number[]>([]);

  const reset = () => {
    const eng = engineRef.current;
    if (!eng) return;
    while (eng.melodyOscs.length > 1) {
      const osc = eng.melodyOscs.pop()!;
      eng.melodyGains.pop();
      try { osc.stop(); } catch {}
    }
    while (eng.droneOscs.length > 1) {
      const osc = eng.droneOscs.pop()!;
      eng.droneGains.pop();
      try { osc.stop(); } catch {}
    }
    eng.beatPresetIndex = 0;
    totalRef.current = 0;
    setXVoices(1);
    setBeatPreset(0);
    setZLayers(1);
    setTotalInteractions(0);
  };

  useEffect(() => {
    const eng = createAxisEngine();
    engineRef.current = eng;
    startBeatSequencer(eng, 150);

    const onCC = (evt: CCUpdateEvent) => {
      eng.octaveShift = octaveRef.current;
      eng.speedMul = speedRef.current;
      const t = eng.ctx.currentTime;
      const x = evt.cc === OddballCC.OrientX ? evt.value : cc.orientX;
      const y = evt.cc === OddballCC.OrientY ? evt.value : cc.orientY;
      const z = evt.cc === OddballCC.OrientZ ? evt.value : cc.orientZ;

      // Update history
      if (evt.cc === OddballCC.OrientX || evt.cc === OddballCC.OrientY || evt.cc === OddballCC.OrientZ) {
        const h = histRef.current;
        h.x.push(x); h.y.push(y); h.z.push(z);
        if (h.x.length > HISTORY_LEN) { h.x.shift(); h.y.shift(); h.z.shift(); }
        setHistTick((n) => n + 1);
      }

      const dom = getDominantAxis(x, y, z);
      dominantRef.current = dom;
      setDominant(dom);

      const dx = Math.abs(x - 64) / 64;
      const dy = Math.abs(y - 64) / 64;
      const dz = Math.abs(z - 64) / 64;
      const total = Math.max(dx + dy + dz, 0.01);

      eng.melodyMaster.gain.setTargetAtTime(Math.min(1, (dx / total) * 0.8 + 0.1), t, 0.05);
      eng.rhythmMaster.gain.setTargetAtTime(Math.min(1, (dy / total) * 0.6 + 0.35), t, 0.05);
      eng.droneMaster.gain.setTargetAtTime(Math.min(1, (dz / total) * 0.8 + 0.1), t, 0.05);

      switch (evt.cc) {
        case OddballCC.OrientX: {
          const degree = Math.floor(mapRange(evt.value, 0, 127, 0, MELODY_SCALE.length - 0.01));
          const octMul = Math.pow(2, eng.octaveShift);
          const freq = MELODY_SCALE[degree] * octMul;
          eng.melodyOscs[0].frequency.setTargetAtTime(freq, t, 0.03);
          for (let i = 1; i < eng.melodyOscs.length; i++) {
            const intervals = [1.25, 1.5];
            eng.melodyOscs[i].frequency.setTargetAtTime(freq * intervals[i - 1], t, 0.03);
          }
          break;
        }
        case OddballCC.OrientY: {
          const bpm = mapRange(evt.value, 0, 127, 70, 180);
          const stepMs = Math.round(60000 / bpm / 4 / eng.speedMul);
          if (Math.abs(stepMs - stepMsRef.current) > 10) {
            stepMsRef.current = stepMs;
            startBeatSequencer(eng, stepMs);
          }
          break;
        }
        case OddballCC.OrientZ: {
          const cutoff = mapRange(evt.value, 0, 127, 300, 3000);
          eng.droneFilter.frequency.setTargetAtTime(cutoff, t, 0.05);
          break;
        }
        case OddballCC.Energy: {
          const vol = mapRange(evt.value, 0, 127, 0.1, 0.5);
          eng.masterGain.gain.setTargetAtTime(vol, t, 0.1);
          break;
        }
      }

      if (totalRef.current > 0) {
        const filterBoost = Math.min(totalRef.current * 50, 2000);
        eng.melodyFilter.frequency.setTargetAtTime(3000 + filterBoost, t, 0.2);
      }
    };

    const onTap = (_evt: TapEvent) => {
      const now = Date.now();

      // Track rapid taps for reset (5 taps within 2s)
      tapTimesRef.current.push(now);
      tapTimesRef.current = tapTimesRef.current.filter((t) => now - t < 2000);
      if (tapTimesRef.current.length >= 5) {
        tapTimesRef.current = [];
        reset();
        setLastGesture({ type: 'reset', axis: '', time: now });
        return;
      }

      totalRef.current++;
      setTotalInteractions(totalRef.current);
      const dom = dominantRef.current;
      setLastGesture({ type: 'tap', axis: dom, time: now });

      // Add voice if X-dominant
      if (dom === 'x') {
        addMelodyVoice(eng);
        setXVoices(eng.melodyOscs.length);
      }

      // "Wave" effect: sweep filter up then back down, briefly boost volume
      const t = eng.ctx.currentTime;
      eng.melodyFilter.frequency.cancelScheduledValues(t);
      eng.melodyFilter.frequency.setValueAtTime(eng.melodyFilter.frequency.value, t);
      eng.melodyFilter.frequency.linearRampToValueAtTime(6000, t + 0.15);
      eng.melodyFilter.frequency.linearRampToValueAtTime(3000 + Math.min(totalRef.current * 50, 2000), t + 0.5);

      eng.droneFilter.frequency.cancelScheduledValues(t);
      eng.droneFilter.frequency.setValueAtTime(eng.droneFilter.frequency.value, t);
      eng.droneFilter.frequency.linearRampToValueAtTime(4000, t + 0.12);
      eng.droneFilter.frequency.exponentialRampToValueAtTime(Math.max(300, eng.droneFilter.frequency.value), t + 0.6);

      // Brief volume swell
      eng.masterGain.gain.cancelScheduledValues(t);
      eng.masterGain.gain.setValueAtTime(eng.masterGain.gain.value, t);
      eng.masterGain.gain.linearRampToValueAtTime(Math.min(eng.masterGain.gain.value * 1.5, 0.6), t + 0.08);
      eng.masterGain.gain.setTargetAtTime(0.35, t + 0.1, 0.2);
    };

    const onShake = (_evt: ShakeEvent) => {
      totalRef.current++;
      setTotalInteractions(totalRef.current);
      const dom = dominantRef.current;
      setLastGesture({ type: 'shake', axis: dom, time: Date.now() });
      if (dom === 'y') {
        eng.beatPresetIndex = (eng.beatPresetIndex + 1) % BEAT_PRESETS.length;
        setBeatPreset(eng.beatPresetIndex);
        startBeatSequencer(eng, stepMsRef.current);
      }
    };

    const onTwist = (_evt: TwistEvent) => {
      totalRef.current++;
      setTotalInteractions(totalRef.current);
      const dom = dominantRef.current;
      setLastGesture({ type: 'twist', axis: dom, time: Date.now() });
      if (dom === 'z') {
        addDroneLayer(eng);
        setZLayers(eng.droneOscs.length);
      }
    };

    parser.on('cc', onCC);
    parser.on('tap', onTap);
    parser.on('shake', onShake);
    parser.on('twist', onTwist);

    return () => {
      parser.off('cc', onCC);
      parser.off('tap', onTap);
      parser.off('shake', onShake);
      parser.off('twist', onTwist);
      clearInterval(eng.rhythmInterval);
      eng.melodyOscs.forEach((o) => { try { o.stop(); } catch {} });
      eng.droneOscs.forEach((o) => { try { o.stop(); } catch {} });
      eng.masterGain.disconnect();
    };
  }, [parser, detector]);

  const COLORS = { x: '#e85', y: '#5b8', z: '#58e' } as const;

  // Derived display values
  const noteDeg = Math.floor(mapRange(cc.orientX, 0, 127, 0, MELODY_SCALE.length - 0.01));
  const noteName = NOTE_NAMES[Math.max(0, Math.min(noteDeg, NOTE_NAMES.length - 1))];
  const pulseBPM = Math.round(mapRange(cc.orientY, 0, 127, 70, 180));
  const droneCutoff = Math.round(mapRange(cc.orientZ, 0, 127, 300, 3000));

  const gestureRecent = lastGesture && (Date.now() - lastGesture.time < 800);
  const gestureAxisMatch = (axis: string) => gestureRecent && lastGesture!.axis === axis;

  const axisInfo = [
    {
      key: 'x' as const, label: 'X — Melodic', value: cc.orientX,
      complexity: xVoices, max: 3,
      paramLabel: `Note: ${noteName}`,
      gesture: 'tap', gestureLabel: 'Tap +voice',
    },
    {
      key: 'y' as const, label: 'Y — Rhythmic', value: cc.orientY,
      complexity: beatPreset + 1, max: BEAT_PRESETS.length,
      paramLabel: `${BEAT_PRESETS[beatPreset].name} ${pulseBPM}bpm`,
      gesture: 'shake', gestureLabel: 'Shake = next beat',
    },
    {
      key: 'z' as const, label: 'Z — Textural', value: cc.orientZ,
      complexity: zLayers, max: 5,
      paramLabel: `Cutoff: ${droneCutoff}Hz`,
      gesture: 'twist', gestureLabel: 'Twist +layer',
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', paddingTop: '24px' }}>
      <h2 style={{ fontSize: '1rem', fontWeight: 300, color: '#888' }}>Axis Explorer</h2>

      {/* Dominance mix bar */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
        <div style={{ display: 'flex', gap: '16px', fontSize: '0.6rem', color: '#555' }}>
          <span style={{ color: COLORS.x }}>X</span>
          <span style={{ color: COLORS.y }}>Y</span>
          <span style={{ color: COLORS.z }}>Z</span>
        </div>
        <DominanceMix x={cc.orientX} y={cc.orientY} z={cc.orientZ} colors={[COLORS.x, COLORS.y, COLORS.z]} />
      </div>

      {/* Three axis panels */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {axisInfo.map((axis) => {
          const isDom = dominant === axis.key;
          const color = COLORS[axis.key];
          const flashing = gestureAxisMatch(axis.key);
          return (
            <div key={axis.key} style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
              padding: '12px 14px', borderRadius: '8px', minWidth: '140px',
              background: flashing ? `${color}33` : isDom ? `${color}15` : 'transparent',
              border: `1px solid ${isDom ? color : '#282828'}`,
              transition: 'all 0.15s',
            }}>
              {/* Title + dominant badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.7rem', color: isDom ? color : '#666', fontWeight: isDom ? 600 : 400 }}>
                  {axis.label}
                </span>
                {isDom && (
                  <span style={{
                    fontSize: '0.55rem', padding: '1px 5px', borderRadius: '6px',
                    background: `${color}33`, color, fontWeight: 600,
                  }}>
                    DOM
                  </span>
                )}
              </div>

              {/* Deviation bar */}
              <DeviationBar value={axis.value} color={color} isDominant={isDom} />

              {/* Raw value */}
              <div style={{ fontSize: '1.1rem', fontFamily: 'monospace', color: isDom ? color : '#777' }}>
                {axis.value}
              </div>

              {/* Sparkline */}
              <DeviationSparkline data={histRef.current[axis.key]} color={color} />

              {/* Sound parameter */}
              <div style={{
                fontSize: '0.65rem', fontFamily: 'monospace', color: isDom ? color : '#555',
                padding: '2px 8px', background: '#1a1a1a', borderRadius: '4px',
              }}>
                {axis.paramLabel}
              </div>

              {/* Complexity dots */}
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                {Array.from({ length: axis.max }, (_, i) => (
                  <div key={i} style={{
                    width: '7px', height: '7px', borderRadius: '50%',
                    background: i < axis.complexity ? color : '#282828',
                    transition: 'background 0.2s',
                  }} />
                ))}
                <span style={{ fontSize: '0.55rem', color: '#555', marginLeft: '4px' }}>
                  {axis.gestureLabel}
                </span>
              </div>

              {/* Gesture flash */}
              {flashing && (
                <div style={{
                  fontSize: '0.65rem', fontWeight: 600, color: '#111',
                  background: color, padding: '2px 10px', borderRadius: '8px',
                }}>
                  {lastGesture!.type.toUpperCase()}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer row */}
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
        <span style={{ fontSize: '0.75rem', color: '#666' }}>
          Interactions: {totalInteractions}
        </span>
        <button
          onClick={reset}
          style={{
            padding: '4px 14px', borderRadius: '10px', border: '1px solid #444',
            background: 'transparent', color: '#999', cursor: 'pointer', fontSize: '0.7rem',
          }}
        >
          Reset
        </button>
      </div>

      <div style={{ fontSize: '0.65rem', color: '#444', maxWidth: '340px', textAlign: 'center', lineHeight: 1.6 }}>
        Tilt toward an axis to make it dominant &middot; Dominant axis is loudest
        <br />
        Gestures while tilted add complexity to that axis's voice
      </div>
    </div>
  );
}
